import { graphql } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import { css } from '@emotion/react';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { colors } from '../../primitives/colors';
import RichText from '../../primitives/rich-text';
import Container from '../../primitives/grid/container';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import QuickLinks from './quick-links';
import { breakpoints } from '../../primitives/tokens';
import Row from '../../primitives/grid/row';
import Col from '../../primitives/grid/col';
import Typography from '../../primitives/typography';

const styles = {
    background: css({
        paddingTop: '64px',
        paddingBottom: '64px',
        position: 'relative',
        zIndex: 10,
        backgroundColor: colors.grey100,
        [breakpoints.mb]: {
            paddingTop: '32px',
            paddingBottom: '32px',
        },
    }),
    container: css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        [breakpoints.tb]: {
            display: 'block',
        },
    }),
    contentWrapLeft: css({
        width: '50%',
        [breakpoints.tb]: {
            width: '100%',
        },
    }),
    contentWrapRight: css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '50%',
        [breakpoints.tb]: {
            width: '100%',
        },
    }),
    headline: css({
        fontSize: '54px',
        lineHeight: 1,
        marginBottom: '20px',
        marginRight: 'auto',
        marginLeft: 'auto',
        [breakpoints.tb]: {
            fontSize: '36px',
        },
    }),
    copy: css({
        maxWidth: '768px',
        fontSize: '20px',
        lineHeight: '28px',
        whiteSpace: 'pre-line',
        marginRight: 'auto',
        marginLeft: 'auto',
        [breakpoints.tb]: {
            fontSize: '18px',
        },
    }),
};

const ServicesDetail: React.FC<Queries.ServicesDetailDataFragment> = (data) => {
    const {
        headline,
        copy: originalCopy,
        quicklinks,
        overrideWithUniqueContent: override,
    } = data;
    const { isMicrosite, uniqueContent, franchise } = useMicrosite();

    const hasQuickLinks = quicklinks?.links && quicklinks?.links?.length > 0;

    const copy = (
        (override && isMicrosite) ? uniqueContent[override]?.rawContent ?? originalCopy : originalCopy
    ) || '';

    const overrideUsed = isMicrosite && override && copy === uniqueContent[override]?.rawContent;

    return (
        <div css={styles.background}>
            <Container css={styles.container}>
                <Row>
                    <Col breakpoints={{ dt: { span: 6 } }}>
                        <Typography
                            as="h2"
                            fontWeight="regular"
                            reducedLetterSpacing
                            fontSize={{
                                dt: '5xl',
                                tb: '4xl',
                                mb: '3xl',
                            }}
                            css={[{
                                marginBottom: '24px',
                                [breakpoints.tb]: {
                                    textAlign: 'left',
                                    maxWidth: '80%',
                                },
                            }]}
                            dangerouslySetInnerHTML={{
                                __html: apCaseOnlyTitleTags(headline),
                            }}
                        />
                        <RichText maxLength={overrideUsed ? 12 : undefined} css={styles.copy}>
                            {copy}
                        </RichText>
                    </Col>
                    <Col breakpoints={{ dt: { span: 1 } }} />
                    <Col breakpoints={{ dt: { span: 5 } }}>
                        {hasQuickLinks && <QuickLinks {...quicklinks} />}
                        {isMicrosite && override === 'index' && franchise && franchise.franchiseNumber === 11330 && (
                            <StaticImage
                                sizes="(max-width: 400px) 800px, (max-width: 900px) 1200px, (max-width: 1200px) 1600px"
                                css={{
                                    maxHeight: 400,
                                    maxWidth: 400,
                                    borderRadius: '30px',
                                    margin: 'auto',
                                }}
                                alt="Certificates"
                                formats={['auto', 'webp']}
                                src="https://franchiseadmin.servpro.com/FranchiseContent/NewsPhotos/11330-6fad8462-97e7-4c41-9f6d-5f4e7815e5f7.png"
                            />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export const query = graphql`
    fragment ServicesDetailData on CsServicesDetail {
        ... on CsServicesDetail {
            headline
            copy
            overrideWithUniqueContent
            quicklinks {
                heading
                links {
                    href
                    title
                }
            }
        }
    }
`;

export default ServicesDetail;
